/**
 * AIThinkingCard Component
 *
 * A modern, visually polished thinking indicator for AI agent operations.
 * Features:
 * - Animated Lottie AI brain icon
 * - Smooth fade transitions between thinking updates
 * - Persists last thinking text until new one arrives
 * - Optional cancel button
 * - Glassmorphism/gradient background
 */

import React, { useEffect, useRef, useState } from 'react';
import { cn } from '@/lib/utils';
import { X } from 'lucide-react';
import { Button } from '@/components/ui/button';


interface AIThinkingCardProps {
  /** Current thinking text from the agent */
  thinking?: string | null;
  /** Whether the card should be visible */
  isActive: boolean;
  /** Callback when cancel button is clicked */
  onCancel?: () => void;
  /** Color variant - 'default' for primary, 'blue' for edit operations */
  variant?: 'default' | 'blue';
  /** Additional CSS classes */
  className?: string;
}

// AI Brain Lottie animation (from LottieFiles, free to use)
const AI_THINKING_LOTTIE = 'https://assets-v2.lottiefiles.com/a/d5543bc0-a8a5-11ee-a44f-7bc5f99a0a60/g1j2POcLI9.json';

/**
 * AIThinkingCard - Modern thinking indicator for AI operations
 *
 * Displays an animated Lottie icon with thinking text that smoothly
 * fades between updates. The last thinking text persists until a new
 * one arrives, preventing the jarring disappear/reappear effect.
 */
export const AIThinkingCard: React.FC<AIThinkingCardProps> = ({
  thinking,
  isActive,
  onCancel,
  variant = 'default',
  className,
}) => {
  const scriptLoadedRef = useRef(false);
  const [displayedThinking, setDisplayedThinking] = useState<string>('');
  const [isTransitioning, setIsTransitioning] = useState(false);

  // Load lottie-player script on demand
  useEffect(() => {
    if (scriptLoadedRef.current) return;

    const existingScript = document.querySelector('script[src*="lottie-player"]');
    if (existingScript) {
      scriptLoadedRef.current = true;
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://unpkg.com/@lottiefiles/lottie-player@2.0.12/dist/lottie-player.js';
    script.async = true;
    document.head.appendChild(script);
    scriptLoadedRef.current = true;
  }, []);

  // Smooth transition when thinking text changes
  useEffect(() => {
    if (!thinking) return; // Don't clear - persist last thinking

    if (thinking !== displayedThinking) {
      // Fade out
      setIsTransitioning(true);
      const timer = setTimeout(() => {
        // Update text and fade in
        setDisplayedThinking(thinking);
        setIsTransitioning(false);
      }, 150); // Brief fade-out duration
      return () => clearTimeout(timer);
    }
  }, [thinking, displayedThinking]);

  // Initialize displayedThinking on first render
  useEffect(() => {
    if (isActive && thinking && !displayedThinking) {
      setDisplayedThinking(thinking);
    }
  }, [isActive, thinking, displayedThinking]);

  // Reset when becoming inactive
  useEffect(() => {
    if (!isActive) {
      setDisplayedThinking('');
      setIsTransitioning(false);
    }
  }, [isActive]);

  if (!isActive) return null;

  const colors = variant === 'blue'
    ? 'border-primary/30 bg-primary/20'
    : 'border-primary/20 bg-gradient-to-br from-primary/5 via-primary/10 to-primary/5 dark:from-primary/10 dark:via-primary/15 dark:to-primary/10';

  const textColor = variant === 'blue'
    ? 'text-accent-foreground'
    : 'text-foreground/90';

  return (
    <div
      className={cn(
        'rounded-xl border p-4 backdrop-blur-sm shadow-sm',
        'transition-all duration-300 ease-out',
        colors,
        className
      )}
    >
      <div className="flex items-start gap-3">
        {/* Lottie Animation */}
        <div className="flex-shrink-0 w-10 h-10 rounded-lg overflow-hidden bg-white/50 dark:bg-black/20 flex items-center justify-center">
          <lottie-player
            src={AI_THINKING_LOTTIE}
            background="transparent"
            speed="1"
            style={{ width: '32px', height: '32px' }}
            loop
            autoplay
          />
        </div>

        {/* Thinking Text with Fade Transition */}
        <div className="flex-1 min-w-0 pt-0.5">
          <p
            className={cn(
              'text-sm font-medium leading-relaxed',
              'transition-opacity duration-150 ease-in-out',
              textColor,
              isTransitioning ? 'opacity-0' : 'opacity-100'
            )}
          >
            {displayedThinking || 'Thinking...'}
          </p>
        </div>

        {/* Cancel Button */}
        {onCancel && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onCancel}
            className={cn(
              'flex-shrink-0 h-7 w-7 p-0 rounded-full',
              'text-muted-foreground/60 hover:text-destructive hover:bg-destructive/10',
              'transition-colors duration-200'
            )}
            title="Cancel"
          >
            <X className="h-4 w-4" />
          </Button>
        )}
      </div>
    </div>
  );
};

export default AIThinkingCard;
